import { useEffect, useMemo } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Timestamp, type ActivityCreate } from "@travelix/core";

import { useTrip } from "@/hooks/use-trip";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import { CategoryPicker } from "@/components/category-picker";
import { useAuth } from "@/lib/auth-provider";
import { isTripReadOnly } from "@/lib/trip-readonly";
import {
  getTripDateBounds,
  isWithinBounds,
  type TripDateBounds,
} from "@/lib/trip-date-bounds";
import { createActivity, markIdeaScheduled, unwrap, queryKeys, displayNameOf } from "@/lib/api";
import { useTripMutation } from "@/hooks/use-trip-mutation";

// ─── Schema ──────────────────────────────────────────────────
// Trip-date bounds must be closed over at form-creation time — they change
// per-trip. Factored as a schema builder so both add-event and edit-event
// can share the shape.
export function buildEventSchema(bounds: TripDateBounds) {
  return z
    .object({
      name: z.string().trim().min(1, "What's it called?"),
      place: z.string().trim().optional(),
      description: z.string().trim().optional(),
      startTime: z.string().optional(),
      endTime: z.string().optional(),
      category: z
        .enum(["stay", "place", "food", "concert", "movie", "beach", "photo"])
        .nullable()
        .optional(),
    })
    .refine(
      (v) => isWithinBounds(v.startTime, bounds),
      {
        message: `Start must be between ${bounds.startLabel} and ${bounds.endLabel}`,
        path: ["startTime"],
      },
    )
    .refine(
      (v) => isWithinBounds(v.endTime, bounds),
      {
        message: `End must be between ${bounds.startLabel} and ${bounds.endLabel}`,
        path: ["endTime"],
      },
    )
    .refine(
      (v) =>
        !v.startTime ||
        !v.endTime ||
        new Date(v.endTime) >= new Date(v.startTime),
      { message: "End can't be before start", path: ["endTime"] },
    );
}

// ─── Route ───────────────────────────────────────────────────
export default function AddEventRoute() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { data: trip, isLoading } = useTrip(id);

  // Past trips are read-only. Bounce direct URL access back to the timeline
  // with a one-liner toast so it doesn't feel like the click did nothing.
  const readOnly = isTripReadOnly(trip);
  useEffect(() => {
    if (readOnly && id) {
      toast.info("This trip is in the past — read-only.");
    }
  }, [readOnly, id]);
  if (readOnly && id) {
    return <Navigate to={`/trip/${id}/timeline`} replace />;
  }

  // Optional query params:
  //   ?name=…&ideaId=…    from Plan → Ideas → Schedule
  //   ?date=YYYY-MM-DD    from Timeline empty-day "Plan this day" CTA
  //                       — prefills the start/end at 09:00 / 10:00 on
  //                       that day so the form opens ready to save.
  const prefillName = searchParams.get("name") ?? "";
  const linkedIdeaId = searchParams.get("ideaId") ?? undefined;
  const prefillDate = searchParams.get("date") ?? undefined;

  const close = () => navigate(`/trip/${id}/timeline`, { replace: true });

  return (
    <Sheet open guardUnsaved onOpenChange={(open) => !open && close()}>
      <SheetContent>
        <SheetHeader
          title="Add event"
          description={
            trip
              ? `Anything that happens during ${trip.name}. Between ${getTripDateBounds(trip).startLabel} and ${getTripDateBounds(trip).endLabel}.`
              : "A stop, a meal, a check-in — anything worth marking on the timeline."
          }
        />
        {isLoading || !trip ? (
          <SheetBody>
            <Skeleton className="h-64 w-full rounded-md" />
          </SheetBody>
        ) : (
          <AddEventForm
            trip={trip}
            onDone={close}
            prefillName={prefillName}
            linkedIdeaId={linkedIdeaId}
            prefillDate={prefillDate}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Form ────────────────────────────────────────────────────
function AddEventForm({
  trip,
  onDone,
  prefillName = "",
  linkedIdeaId,
  prefillDate,
}: {
  trip: NonNullable<ReturnType<typeof useTrip>["data"]>;
  onDone: () => void;
  prefillName?: string;
  linkedIdeaId?: string;
  prefillDate?: string;
}) {
  const { user } = useAuth();
  const bounds = getTripDateBounds(trip);

  // Build datetime-local strings for start/end when a date is prefilled.
  // 09:00 is a friendly default that reads as "morning of that day"; the
  // user still edits the time before saving.
  const { defaultStart, defaultEnd } = useMemo(() => {
    if (!prefillDate) return { defaultStart: "", defaultEnd: "" };
    // Guard against a bogus ?date=xxx — only pass through when it matches yyyy-MM-dd.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(prefillDate)) return { defaultStart: "", defaultEnd: "" };
    return { defaultStart: `${prefillDate}T09:00`, defaultEnd: `${prefillDate}T10:00` };
  }, [prefillDate]);

  const form = useForm<z.infer<ReturnType<typeof buildEventSchema>>>({
    resolver: zodResolver(buildEventSchema(bounds)),
    defaultValues: {
      name: prefillName,
      place: "",
      description: "",
      startTime: defaultStart,
      endTime: defaultEnd,
      category: null,
    },
  });
  const category = form.watch("category") ?? null;

  const mutation = useTripMutation({
    keys: [queryKeys.activities(trip.id), queryKeys.trip(trip.id)],
    mutationFn: async (values: z.infer<ReturnType<typeof buildEventSchema>>) => {
      if (!user?.uid) throw new Error("Not signed in");
      const now = Timestamp.now();
      const payload: ActivityCreate = {
        name: values.name.trim(),
        place: values.place?.trim() || null,
        description: values.description?.trim() || null,
        startTime: values.startTime ? Timestamp.fromDate(new Date(values.startTime)) : null,
        endTime: values.endTime ? Timestamp.fromDate(new Date(values.endTime)) : null,
        durationHours:
          values.startTime && values.endTime
            ? Math.max(
                0,
                (new Date(values.endTime).getTime() - new Date(values.startTime).getTime()) /
                  3_600_000,
              )
            : null,
        latitude: null,
        longitude: null,
        category: values.category ?? null,
        plannerId: user.uid,
        plannerName: displayNameOf(user),
        createdAt: now,
        updatedAt: now,
      };
      const data = await unwrap(createActivity(trip.id, payload));

      // If this activity was scheduled from an idea, mark the idea as scheduled.
      // Best-effort — a failure here doesn't undo the activity write.
      if (linkedIdeaId) {
        await markIdeaScheduled(trip.id, linkedIdeaId, data.id);
      }
      return data;
    },
    successToast: "Added to the timeline.",
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't add the event.");
    },
    onSuccess: onDone,
  });

  return (
    <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
      <SheetBody className="flex flex-col gap-4">
        <Field id="ev-name" label="What is it?" error={form.formState.errors.name?.message}>
          <Input
            id="ev-name"
            placeholder="Dinner at Bar Cañete"
            autoFocus
            invalid={!!form.formState.errors.name}
            {...form.register("name")}
          />
        </Field>
        <Field id="ev-category" label="Category">
          <CategoryPicker
            value={category}
            onChange={(next) => form.setValue("category", next, { shouldDirty: true })}
          />
        </Field>
        <Field id="ev-place" label="Where?" error={form.formState.errors.place?.message}>
          {/* Light autocomplete: trip destinations pre-populate the
              browser's own datalist. No third-party API — a natural
              typeahead based on cities the user already added when
              they created the trip. */}
          <Input
            id="ev-place"
            placeholder={
              trip.destinations[0] ? `${trip.destinations[0]}` : "El Raval, Barcelona"
            }
            list="ev-place-suggestions"
            {...form.register("place")}
          />
          <datalist id="ev-place-suggestions">
            {trip.destinations.map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field id="ev-start" label="Starts" error={form.formState.errors.startTime?.message}>
            <Input
              id="ev-start"
              type="datetime-local"
              min={bounds.minAttr}
              max={bounds.maxAttr}
              {...form.register("startTime")}
            />
          </Field>
          <Field id="ev-end" label="Ends" error={form.formState.errors.endTime?.message}>
            <Input
              id="ev-end"
              type="datetime-local"
              min={bounds.minAttr}
              max={bounds.maxAttr}
              {...form.register("endTime")}
            />
          </Field>
        </div>
        <Field
          id="ev-desc"
          label="Anything else? (optional)"
          helper="Notes, dress code, reservation number — anything the group should see."
          error={form.formState.errors.description?.message}
        >
          <Input
            id="ev-desc"
            placeholder="Bring cash, tipping expected"
            {...form.register("description")}
          />
        </Field>
      </SheetBody>
      <SheetFooter>
        <Button type="submit" block disabled={mutation.isPending}>
          {mutation.isPending ? "Adding…" : "Add to timeline"}
        </Button>
        <Button type="button" variant="ghost" block onClick={onDone} disabled={mutation.isPending}>
          Cancel
        </Button>
      </SheetFooter>
    </form>
  );
}
